import { forwardRef } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { closedLink } from './linkFallback';

/**
 * A drop-in `Link` for pages that link to other screens (permissions brief,
 * decision 1: a screen the person may not open does not exist for them).
 *
 * - The path is open: a normal `Link`.
 * - The path is closed and points into a screen (a project, a quote): the
 *   same content as plain text, without the link colour, underline or hover
 *   (`linkFallback`), so a project name still reads as a name but is not a
 *   way in. A button-styled link (`btn`) is an action and disappears.
 * - The path is closed and is the screen itself (`/projects`, "לכל
 *   הקריאות", a breadcrumb "פרויקטים"): its text names the screen, so it
 *   disappears.
 *
 * Files import it as `import { ScreenLink as Link } from …` so their JSX
 * stays the same. `closed="hide"` removes a link whose text names the screen
 * ("לכל הקריאות", "הצגת הכול") instead of leaving it as text.
 */
export type ScreenLinkProps = LinkProps & { closed?: 'text' | 'hide' };

export const ScreenLink = forwardRef<HTMLAnchorElement, ScreenLinkProps>(function ScreenLink({ closed = 'text', ...props }, ref) {
  const { canPath } = usePermissions();
  const to = typeof props.to === 'string' ? props.to : (props.to.pathname ?? '');
  if (!to || canPath(to)) return <Link ref={ref} {...props} />;
  const fallback = closedLink(to, typeof props.className === 'string' ? props.className : undefined, closed);
  if (fallback === null) return null;
  return <span className={fallback || undefined} lang={props.lang} dir={props.dir}>{props.children}</span>;
});

export default ScreenLink;
